import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { CheckCircle, ArrowRight, Spinner } from "@phosphor-icons/react";

export default function PodSuccessPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("checking");
  const [tier, setTier] = useState(null);
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    if (!sessionId) { setStatus("missing"); return; }
    let cancelled = false;
    const poll = async () => {
      if (cancelled) return;
      try {
        const { data } = await api.post(`/pod/checkout/confirm/${sessionId}`);
        if (cancelled) return;
        if (data.paid) {
          setStatus("paid");
          setTier(data.tier);
          return;
        }
        if (attempts >= 12) { setStatus("timeout"); return; }
        setAttempts(a => a + 1);
        setTimeout(poll, 2500);
      } catch (e) {
        if (cancelled) return;
        setStatus("error");
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId, attempts]);

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6" data-testid="pod-success-page">
      <div className="max-w-md bg-white border border-slate-200 p-8 text-center">
        {status === "checking" && (
          <>
            <Spinner size={48} className="text-blue-700 animate-spin mx-auto mb-4" />
            <h1 className="text-2xl font-bold mb-2 text-slate-950" style={{ fontFamily: "'Outfit', sans-serif" }}>Confirming your payment…</h1>
            <p className="text-sm text-slate-600">Please don't close this window. This usually takes 5–10 seconds.</p>
          </>
        )}
        {status === "paid" && (
          <>
            <CheckCircle size={56} weight="fill" className="text-emerald-600 mx-auto mb-4" />
            <h1 className="text-2xl font-bold mb-2 text-slate-950" style={{ fontFamily: "'Outfit', sans-serif" }}>Welcome to the POD!</h1>
            <p className="text-sm text-slate-600 mb-2">Your <b>{tier?.replace("_", "-")}</b> membership is active.</p>
            <p className="text-xs text-slate-500 mb-6">You now have access to webinars, the resource library, AI template generator and the roundtable community.</p>
            <div className="flex gap-2 justify-center">
              <Link to="/pod/webinars"><Button className="bg-blue-800 text-white hover:bg-blue-900 rounded-none gap-2" data-testid="pod-success-explore">Open POD <ArrowRight size={14} weight="bold" /></Button></Link>
              <Link to="/dashboard"><Button variant="outline" className="rounded-none">Dashboard</Button></Link>
            </div>
          </>
        )}
        {(status === "timeout" || status === "error" || status === "missing") && (
          <>
            <h1 className="text-2xl font-bold mb-2 text-slate-950" style={{ fontFamily: "'Outfit', sans-serif" }}>Hmm, that's taking longer than expected.</h1>
            <p className="text-sm text-slate-600 mb-6">
              {status === "missing" ? "Session ID missing." : "We couldn't confirm payment yet. If you completed it, please refresh — or check your dashboard."}
            </p>
            <div className="flex gap-2 justify-center">
              <Button onClick={() => window.location.reload()} className="bg-blue-800 text-white hover:bg-blue-900 rounded-none">Retry</Button>
              <Link to="/pod"><Button variant="outline" className="rounded-none">Back to POD</Button></Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
